import React from "react";
import { Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { colors, FONT } from "../../styles";

const questionnaireColors = {
    numberBg: "#E8F0FE",
    title: "#11234E",
    cardBorder: "#E5E7EB",
    nestedBg: "#F3F6FB",
    yesBg: "#E8F8EF",
    yesText: "#1B7A4A",
    yesIcon: "#22A06B",
    noBg: "#FDECEC",
    noText: "#C0392B",
    noIcon: "#E74C3C",
};

const styles = StyleSheet.create({
    section: {
        marginBottom: 18,
    },
    sectionDivider: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        borderBottomStyle: "solid",
        marginTop: 16,
        marginBottom: 4,
    },
    sectionHeader: {
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 14,
    },
    sectionNumberBox: {
        width: 28,
        height: 28,
        borderRadius: 6,
        backgroundColor: questionnaireColors.numberBg,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 10,
    },
    sectionNumberText: {
        fontSize: FONT.title,
        fontWeight: 700,
        color: colors.primary,
    },
    sectionTitleWrap: {
        flexGrow: 1,
        flexShrink: 1,
        paddingTop: 2,
    },
    sectionTitle: {
        fontSize: FONT.title,
        fontWeight: 700,
        color: questionnaireColors.title,
        marginBottom: 6,
    },
    sectionTitleUnderline: {
        width: 42,
        height: 3,
        backgroundColor: colors.primary,
        borderRadius: 2,
    },
    subSection: {
        marginBottom: 10,
    },
    subSectionTitle: {
        fontSize: FONT.subtitle,
        fontWeight: 700,
        color: colors.primary,
        marginBottom: 8,
        marginTop: 4,
    },
    questionCard: {
        borderWidth: 1,
        borderColor: questionnaireColors.cardBorder,
        borderStyle: "solid",
        borderRadius: 8,
        overflow: "hidden",
        marginBottom: 10,
        backgroundColor: colors.white,
    },
    questionRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        paddingVertical: 10,
        paddingHorizontal: 10,
        backgroundColor: colors.white,
    },
    questionRowAlt: {
        backgroundColor: colors.rowAlt,
    },
    questionNumberBox: {
        minWidth: 28,
        paddingHorizontal: 4,
        height: 22,
        borderRadius: 5,
        backgroundColor: questionnaireColors.numberBg,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 8,
        marginTop: 1,
    },
    questionNumberText: {
        fontSize: FONT.small,
        fontWeight: 700,
        color: colors.primary,
    },
    questionBody: {
        flexGrow: 1,
        flexShrink: 1,
        paddingRight: 8,
    },
    questionTitle: {
        fontSize: FONT.body,
        fontWeight: 400,
        color: colors.primaryText,
        lineHeight: 1.35,
    },
    answerAside: {
        alignItems: "flex-end",
        justifyContent: "center",
        maxWidth: "34%",
        marginTop: 1,
    },
    badge: {
        flexDirection: "row",
        alignItems: "center",
        borderRadius: 12,
        paddingVertical: 3,
        paddingHorizontal: 8,
    },
    badgeYes: {
        backgroundColor: questionnaireColors.yesBg,
    },
    badgeNo: {
        backgroundColor: questionnaireColors.noBg,
    },
    badgeNeutral: {
        backgroundColor: colors.rowAlt,
        borderWidth: 1,
        borderColor: colors.border,
        borderStyle: "solid",
    },
    badgeIconWrap: {
        width: 12,
        height: 12,
        borderRadius: 6,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 5,
    },
    badgeIconYes: {
        backgroundColor: questionnaireColors.yesIcon,
    },
    badgeIconNo: {
        backgroundColor: questionnaireColors.noIcon,
    },
    badgeIconNeutral: {
        backgroundColor: colors.primary,
    },
    badgeText: {
        fontSize: FONT.small,
        fontWeight: 700,
    },
    badgeTextYes: {
        color: questionnaireColors.yesText,
    },
    badgeTextNo: {
        color: questionnaireColors.noText,
    },
    badgeTextNeutral: {
        color: colors.primaryText,
    },
    answerText: {
        fontSize: FONT.body,
        fontWeight: 700,
        color: colors.primaryText,
        textAlign: "right",
    },
    nestedBlock: {
        marginHorizontal: 10,
        marginBottom: 10,
        marginTop: 0,
        backgroundColor: questionnaireColors.nestedBg,
        borderRadius: 6,
        paddingVertical: 8,
        paddingHorizontal: 10,
        flexDirection: "row",
        alignItems: "flex-start",
    },
    nestedIconWrap: {
        width: 18,
        height: 18,
        borderRadius: 4,
        backgroundColor: colors.primary,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 8,
        marginTop: 1,
    },
    nestedBody: {
        flexGrow: 1,
        flexShrink: 1,
    },
    nestedPrompt: {
        fontSize: FONT.small,
        color: colors.mutedText,
        marginBottom: 3,
        lineHeight: 1.3,
    },
    nestedAnswer: {
        fontSize: FONT.body,
        fontWeight: 700,
        color: colors.primaryText,
        lineHeight: 1.35,
    },
    optionList: {
        marginTop: 2,
    },
    optionItem: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 3,
    },
    optionBullet: {
        width: 5,
        height: 5,
        borderRadius: 3,
        backgroundColor: colors.primary,
        marginRight: 6,
    },
    optionText: {
        fontSize: FONT.body,
        color: colors.primaryText,
        flexGrow: 1,
        flexShrink: 1,
    },
    emptyAnswer: {
        fontSize: FONT.body,
        color: colors.mutedText,
        fontStyle: "italic",
    },
});

const dateUtils = new DateUtils();

const CheckIcon = ({ color = "#FFFFFF" }) => (
    <Svg width="8" height="8" viewBox="0 0 10 10">
        <Path
            d="M1.5 5.2 L3.8 7.5 L8.5 2.5"
            stroke={color}
            strokeWidth="1.6"
            fill="none"
        />
    </Svg>
);

const CrossIcon = ({ color = "#FFFFFF" }) => (
    <Svg width="7" height="7" viewBox="0 0 10 10">
        <Path
            d="M2 2 L8 8 M8 2 L2 8"
            stroke={color}
            strokeWidth="1.6"
            fill="none"
        />
    </Svg>
);

const CommentIcon = () => (
    <Svg width="10" height="10" viewBox="0 0 24 24">
        <Path
            d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
            fill="#FFFFFF"
        />
    </Svg>
);

const normalizeAnswerLabel = (value) => String(value || "").trim().toLowerCase();

const isYesAnswer = (value) => ["yes", "true", "1"].includes(normalizeAnswerLabel(value));
const isNoAnswer = (value) => ["no", "false", "0"].includes(normalizeAnswerLabel(value));

const findQuestionAnswers = (question) => {
    if (!question) {
        return null;
    }

    switch (question.type) {
        case "textarea":
        case "text":
        case "date":
        case "select":
        case "country_list":
            return question.answer?.[0]?.title;

        case "checkbox":
        case "category":
        case "file_upload":
            return question.answer || [];

        case "radiobox": {
            const userAnswer = Array.isArray(question.answer) && question.answer.length > 0
                ? question.answer[0]
                : null;

            if (!userAnswer) {
                return null;
            }

            return {
                value: userAnswer.value,
                title: userAnswer.title,
                children: userAnswer.children,
            };
        }

        default:
            return null;
    }
};

const AnswerBadge = ({ label, t }) => {
    const translated = t(label);
    const yes = isYesAnswer(label) || isYesAnswer(translated);
    const no = isNoAnswer(label) || isNoAnswer(translated);

    if (yes) {
        return (
            <View style={[styles.badge, styles.badgeYes]}>
                <View style={[styles.badgeIconWrap, styles.badgeIconYes]}>
                    <CheckIcon />
                </View>
                <Text style={[styles.badgeText, styles.badgeTextYes]}>
                    {translated || t("Yes")}
                </Text>
            </View>
        );
    }

    if (no) {
        return (
            <View style={[styles.badge, styles.badgeNo]}>
                <View style={[styles.badgeIconWrap, styles.badgeIconNo]}>
                    <CrossIcon />
                </View>
                <Text style={[styles.badgeText, styles.badgeTextNo]}>
                    {translated || t("No")}
                </Text>
            </View>
        );
    }

    return (
        <View style={[styles.badge, styles.badgeNeutral]}>
            <Text style={[styles.badgeText, styles.badgeTextNeutral]}>
                {translated || "-"}
            </Text>
        </View>
    );
};

const NestedAnswerBlock = ({ prompt, answer, t }) => {
    const answerText = answer === null || answer === undefined || answer === ""
        ? "-"
        : String(answer);

    return (
        <View style={styles.nestedBlock} wrap={false}>
            <View style={styles.nestedIconWrap}>
                <CommentIcon />
            </View>
            <View style={styles.nestedBody}>
                {prompt ? (
                    <Text style={styles.nestedPrompt}>
                        {t(prompt)}
                    </Text>
                ) : null}
                <Text style={styles.nestedAnswer}>
                    {t(answerText)}
                </Text>
            </View>
        </View>
    );
};

const OptionList = ({ items = [], t }) => {
    if (!items.length) {
        return <Text style={styles.emptyAnswer}>-</Text>;
    }

    return (
        <View style={styles.optionList}>
            {items.map((item, index) => (
                <View style={styles.optionItem} key={index}>
                    <View style={styles.optionBullet} />
                    <Text style={styles.optionText}>
                        {t(item)}
                    </Text>
                </View>
            ))}
        </View>
    );
};

const QuestionCard = ({
    number,
    title,
    answerNode,
    nestedNodes = [],
    t,
}) => (
    <View style={styles.questionCard} wrap={false}>
        <View style={styles.questionRow}>
            <View style={styles.questionNumberBox}>
                <Text style={styles.questionNumberText}>{number}</Text>
            </View>
            <View style={styles.questionBody}>
                <Text style={styles.questionTitle}>{t(title)}</Text>
            </View>
            {answerNode ? (
                <View style={styles.answerAside}>
                    {answerNode}
                </View>
            ) : null}
        </View>
        {nestedNodes}
    </View>
);

const ReportContent = ({ sections = [], t }) => {
    const renderSubQuestions = (children, childrenInfos) => {
        if (!Array.isArray(children) || !children.length) {
            return [];
        }

        return children.map((child, childIndex) => {
            const childInfos = (childrenInfos || []).find(
                (info) => parseInt(info.id, 10) === parseInt(child.id, 10)
            );
            const prompt = childInfos?.title || child.title;
            const answer = child.type === "file_upload"
                ? (child.name || "-")
                : (child.title || child.name || "-");

            return (
                <NestedAnswerBlock
                    key={`nested-${childIndex}`}
                    prompt={prompt}
                    answer={answer}
                    t={t}
                />
            );
        });
    };

    const groupQuestionsBySubSection = (items) => {
        const result = [];

        const findSection = (sectionName) => result.find(
            (section) => section.name === sectionName
        );

        (items || []).forEach((question) => {
            const sectionName = question.subSection || "";
            let subSection = findSection(sectionName);

            if (!subSection) {
                subSection = {
                    name: sectionName,
                    items: [],
                };
                result.push(subSection);
            }

            subSection.items.push(question);
        });

        return result;
    };

    const renderPrimaryAnswer = (question, userAnswers) => {
        const { type } = question;

        if (type === "select" || type === "country_list") {
            if (userAnswers === null || userAnswers === undefined || userAnswers === "") {
                return <Text style={styles.emptyAnswer}>-</Text>;
            }

            if (isYesAnswer(userAnswers) || isNoAnswer(userAnswers)) {
                return <AnswerBadge label={userAnswers} t={t} />;
            }

            return <AnswerBadge label={userAnswers} t={t} />;
        }

        if (type === "date") {
            if (!userAnswers) {
                return <Text style={styles.emptyAnswer}>-</Text>;
            }

            return (
                <Text style={styles.answerText}>
                    {dateUtils.convertDateToDate(userAnswers, INTERNATIONAL_DATE_FORMAT)}
                </Text>
            );
        }

        if (type === "radiobox") {
            if (!userAnswers?.title) {
                return <Text style={styles.emptyAnswer}>-</Text>;
            }

            return <AnswerBadge label={userAnswers.title} t={t} />;
        }

        if (type === "checkbox" || type === "category") {
            const labels = (userAnswers || [])
                .map((item) => item?.title)
                .filter(Boolean);

            if (labels.length === 1 && (isYesAnswer(labels[0]) || isNoAnswer(labels[0]))) {
                return <AnswerBadge label={labels[0]} t={t} />;
            }

            return null;
        }

        return null;
    };

    const renderNestedContent = (question, userAnswers) => {
        const { type } = question;
        const nodes = [];

        if (type === "textarea" || type === "text") {
            nodes.push(
                <NestedAnswerBlock
                    key="text-answer"
                    prompt={null}
                    answer={userAnswers || "-"}
                    t={t}
                />
            );
        }

        if (type === "checkbox" || type === "category") {
            const labels = (userAnswers || [])
                .map((item) => item?.title)
                .filter(Boolean);

            if (labels.length > 1 || (labels.length === 1 && !isYesAnswer(labels[0]) && !isNoAnswer(labels[0]))) {
                nodes.push(
                    <View key="checkbox-options" style={{ paddingHorizontal: 10, paddingBottom: 10 }}>
                        <OptionList items={labels} t={t} />
                    </View>
                );
            } else if (!labels.length) {
                nodes.push(
                    <View key="checkbox-empty" style={{ paddingHorizontal: 10, paddingBottom: 10 }}>
                        <Text style={styles.emptyAnswer}>-</Text>
                    </View>
                );
            }
        }

        if (type === "file_upload") {
            const files = (userAnswers || []).map((file) => file?.name).filter(Boolean);
            nodes.push(
                <View key="files" style={{ paddingHorizontal: 10, paddingBottom: 10 }}>
                    <OptionList items={files.length ? files : ["-"]} t={t} />
                </View>
            );
        }

        if (type === "radiobox" && userAnswers) {
            const selectedOption = (question.options || []).find(
                (option) => parseInt(option.id, 10) === parseInt(userAnswers.value, 10)
            );
            const childNodes = renderSubQuestions(
                userAnswers.children || [],
                selectedOption?.children || []
            );
            nodes.push(...childNodes);
        }

        return nodes;
    };

    const renderQuestions = (questions, sectionNumber, startIndex = 0) => {
        return (questions || []).map((question, questionIndex) => {
            const number = `${sectionNumber}.${startIndex + questionIndex + 1}`;
            const userAnswers = findQuestionAnswers(question);
            const answerNode = renderPrimaryAnswer(question, userAnswers);
            const nestedNodes = renderNestedContent(question, userAnswers);

            return (
                <QuestionCard
                    key={`${number}-${question.id || questionIndex}`}
                    number={number}
                    title={question.title}
                    answerNode={answerNode}
                    nestedNodes={nestedNodes}
                    t={t}
                />
            );
        });
    };

    const renderQuestionsGroup = (sectionIndex, items) => {
        const sectionNumber = sectionIndex + 1;
        const subSections = groupQuestionsBySubSection(items);

        if (subSections.length === 1) {
            return renderQuestions(subSections[0].items, sectionNumber);
        }

        let questionOffset = 0;

        return subSections.map((subSection, subSectionIndex) => {
            const rendered = (
                <View style={styles.subSection} key={`sub-${subSectionIndex}`}>
                    {subSection.name ? (
                        <Text style={styles.subSectionTitle}>
                            {t(subSection.name)}
                        </Text>
                    ) : null}
                    {renderQuestions(subSection.items, sectionNumber, questionOffset)}
                </View>
            );

            questionOffset += subSection.items.length;
            return rendered;
        });
    };

    return (
        <>
            {(sections || []).map((section, index) => (
                <View style={styles.section} key={`section-${index}`}>
                    {index > 0 ? <View style={styles.sectionDivider} /> : null}

                    <View style={styles.sectionHeader} wrap={false}>
                        <View style={styles.sectionNumberBox}>
                            <Text style={styles.sectionNumberText}>{index + 1}</Text>
                        </View>
                        <View style={styles.sectionTitleWrap}>
                            <Text style={styles.sectionTitle}>
                                {t(section.name)}
                            </Text>
                            <View style={styles.sectionTitleUnderline} />
                        </View>
                    </View>

                    {renderQuestionsGroup(index, section.items)}
                </View>
            ))}
        </>
    );
};

export default withNamespaces()(ReportContent);
